import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
    AirspaceCheckerParametersWithStartEnd,
    MapAirspaceCheckerService
} from '@flyfreely-portal-ui/airspace';
import {
    AirspaceJurisdictionDto,
    AuthorityFilter,
    AvailableReportDto,
    CreateLocationCommand,
    DO_NOTHING,
    DateFormatService,
    DateRange,
    DownloadService,
    FEATURE_DAILY_OPERATIONS_ADVANCED,
    FlightConformanceResultDto,
    FlyFreelyError,
    FlyFreelyLoggingService,
    GeospatialService,
    GqlFilterField,
    LocationFeatureDto,
    LocationService,
    MissionReportService,
    MissionService,
    MissionSummaryDto,
    NoticesService,
    OperationsSummaryService,
    OrganisationService,
    PersonsOrganisationDto,
    PreferencesService,
    WorkTracker,
    browserTimezone,
    defaultTimezone,
    hasFeatureFlag,
    toTimestamp
} from '@flyfreely-portal-ui/flyfreely';
import { ColumnSortPreferences } from '@flyfreely-portal-ui/flyfreely-table';
import { FullScreenService } from '@flyfreely-portal-ui/fullscreen';
import { toMapFeature } from '@flyfreely-portal-ui/locations';
import {
    FeatureAndGroup,
    FeatureGroup,
    FeaturesAndGroup,
    MapFeature,
    MapViewport
} from '@flyfreely-portal-ui/map';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import { center } from '@turf/center';
import { circle } from '@turf/circle';
import { Angulartics2 } from 'angulartics2';
import { deepEqual } from 'fast-equals';
import { getOrElse } from 'fp-ts/es6/Option';
import { FeatureCollection, Geometry, Point } from 'geojson';
import { AirspaceCheckService } from 'libs/airspace/src/lib/airspace-check';
import { AIRSPACE_DISCLAIMER } from 'libs/locations/src/lib/interfaces';
import { FlyFreelyMapComponent } from 'libs/map/src/lib/flyfreely-map/flyfreely-map.component';
import { pointToLngLat } from 'libs/map/src/lib/utils';
import {
    missionLocationsToFeatureCollection,
    noticeLocationsToFeatureCollection
} from 'libs/missions/src/lib/helpers';
import { MissionDialoguesService } from 'libs/missions/src/lib/mission-dialogues.service';
import { MissionNoticesDataService } from 'libs/notices/src/lib/mission-notices-data.service';
import { LngLat } from 'mapbox-gl';
import moment from 'moment';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ToastrService } from 'ngx-toastr';
import {
    BehaviorSubject,
    Observable,
    ReplaySubject,
    Subject,
    combineLatest
} from 'rxjs';
import {
    debounceTime,
    delay,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil
} from 'rxjs/operators';
import { MissionViewService } from '../mission-view.service';
import { MissionViewDataService } from '../mission-view/mission-view-data.service';

const FLIGHT_AREA_FEATURE_GROUP_ID = 0;
const CHECKER_FEATURE_GROUP_ID = 1;

@Component({
    selector: 'mission-view-map',
    templateUrl: './mission-view-map.component.html',
    styleUrls: ['./mission-view-map.component.scss'],
    providers: [AirspaceCheckService, MapAirspaceCheckerService]
})
export class MissionViewMapComponent implements OnInit {
    @Input() missionAuthorityFilters$: Observable<
        { name: string; children: AuthorityFilter[] }[]
    >;
    @Input() organisationSelector = new FormControl<{
        name: string;
        children: AuthorityFilter[];
    }>(undefined);
    @Input() organisationSelectorValue$: Observable<{
        name: string;
        children: AuthorityFilter[];
    }>;
    @Input() authoritySelector: FormControl<AuthorityFilter> = new FormControl(
        undefined
    );
    @Input() canFilterByAuthority: boolean;
    @Input() canUseNotices: boolean;

    @Output() missionSearchUpdated = new EventEmitter<{
        startTime: string;
        endTime: string;
    }>();

    private featuresSource = new BehaviorSubject<FeatureGroup[]>([]);
    features$ = this.featuresSource.asObservable();

    viewMode: 'MAP' | 'LIST' | 'BOTH';

    mapData$ = new BehaviorSubject<{ [source: string]: FeatureCollection }>({});

    private featureSelected$ = new BehaviorSubject<boolean>(false);
    private mapClicked$ = new Subject<LngLat>();
    isDrawing = false;
    mapIsInEditMode = false;
    useMobileLayout = false;
    reduceButtonSizes = false;

    // Ensure that the map only zooms automatically the first time
    firstRun = true;

    // this can be used to prevent the map zooming to all features, for instance during an airspace check
    // If true it will be set to false after the next time the map emits featuresLoaded
    skipNextFeatureUpdateZoom = false;

    cluster: FeaturesAndGroup;
    selectedLocation: MapFeature;

    flightArea: GeoJSON.Polygon;
    airspaceValue: AirspaceCheckerParametersWithStartEnd;
    checkerAreaRadius = 100;
    mapCenter$ = new BehaviorSubject<MapViewport>(null);
    currentTimeZone: string;
    timeZoneSource: 'MAP' | 'AIRSPACE_CHECK' = 'MAP';
    timeSelection: 'TODAY' | '+2' | '+7' | 'CUSTOM' = 'TODAY';
    updatedTimeFilter$ = new BehaviorSubject<{
        startTime: string;
        endTime: string;
    }>(null);

    missionSearchCriteria: {
        page: number;
        limit: number;
        sorting: ColumnSortPreferences;
        filters: GqlFilterField[];
        organisationId: number;
        conformanceResultStatus: FlightConformanceResultDto.Status[];
        authorityIds?: number[];
        orOrganisationId?: number;
        status?: MissionSummaryDto.Status[];
        startTime: string;
        endTime: string;
        projectId?: number;
    };
    searchTimes$ = new ReplaySubject<{ startTime: string; endTime: string }>(1);

    dateRangeForm: FormGroup<{
        customDateRange: FormControl;
    }>;

    missionLocations$: ReturnType<
        typeof OperationsSummaryService.prototype.findMissionLocations
    >;

    selectedMissions: MissionSummaryDto[];

    missionReportTemplates: AvailableReportDto[];

    private ngUnsubscribe$ = new Subject<void>();

    @ViewChild(FlyFreelyMapComponent)
    private map: FlyFreelyMapComponent;

    @ViewChild(PopoverDirective)
    private authoritySelectionPopover: PopoverDirective;

    jurisdictionHasAirspaceChecker = false;
    airspaceJurisdiction: AirspaceJurisdictionDto;
    showAirspaceDisclaimer = false;
    shouldShowAirspaceDisclaimer$ = new Subject<void>();
    airspaceDisclaimerDates: { [key: string]: Date } = {};
    canAddMission = false;
    canUseAdvancedDailyOperations = false;

    awaitingFeature = false;

    currentOrganisation: PersonsOrganisationDto;
    currentOrganisation$ = new BehaviorSubject<PersonsOrganisationDto>(null);

    private workTracker = new WorkTracker();
    working = false;

    ignoreClicksOnFeatureGroups = [CHECKER_FEATURE_GROUP_ID];

    constructor(
        private operationsSummaryService: OperationsSummaryService,
        private missionViewDataService: MissionViewDataService,
        private preferencesService: PreferencesService,
        private workspaceStateService: WorkspaceStateService,
        private missionService: MissionService,
        private locationService: LocationService,
        private missionDialoguesService: MissionDialoguesService,
        private organisationService: OrganisationService,
        private geospatialService: GeospatialService,
        private missionNoticesDataService: MissionNoticesDataService,
        private noticesService: NoticesService,
        private missionReportService: MissionReportService,
        private downloadService: DownloadService,
        private changeDetector: ChangeDetectorRef,
        private elementRef: ElementRef,
        private fullscreenService: FullScreenService,
        private deviceDetectorService: DeviceDetectorService,
        public missionViewService: MissionViewService,
        private angulartics2: Angulartics2,
        private logging: FlyFreelyLoggingService,
        private toastr: ToastrService,
        private dateFormatService: DateFormatService
    ) {
        this.dateRangeForm = new FormGroup({
            customDateRange: new FormControl(undefined)
        });

        this.workspaceStateService.currentOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                if (organisation.type === 'organisation_loaded') {
                    this.skipNextFeatureUpdateZoom = false;
                    this.currentOrganisation = organisation.organisation;
                    this.currentOrganisation$.next(organisation.organisation);
                    this.currentTimeZone =
                        organisation.organisation.timeZone ?? browserTimezone;
                    this.canUseAdvancedDailyOperations = hasFeatureFlag(
                        this.currentOrganisation,
                        FEATURE_DAILY_OPERATIONS_ADVANCED
                    );
                } else {
                    this.currentOrganisation = null;
                    this.currentOrganisation$.next(null);
                }
            });
    }

    ngOnInit(): void {
        this.setupMissionLocations();
        this.setupListeners();
        this.handleOperationsViewTime();
        this.setupTimeZoneHandlers();
        this.refreshMobileMode();
        this.setupWindowSizingHandler();
    }

    ngOnDestroy() {
        this.workTracker.ngOnDestroy();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.featuresSource.complete();
        this.featureSelected$.complete();
        this.mapClicked$.complete();
        this.shouldShowAirspaceDisclaimer$.complete();
        this.mapCenter$.complete();
        this.updatedTimeFilter$.complete();
        this.searchTimes$.complete();
        this.currentOrganisation$.complete();
    }

    private setupMissionLocations() {
        // We assume that the search criteria is updated when the organisation is changed
        this.missionLocations$ = combineLatest([
            this.missionViewDataService.searchCriteria$.pipe(
                distinctUntilChanged(deepEqual)
            ),
            this.missionService.change$.pipe(startWith(undefined)),
            this.updatedTimeFilter$
        ]).pipe(
            switchMap(([searchCriteria, change, timeFilters]) => {
                const { filters, page, limit, sorting, ...criteria } =
                    searchCriteria;
                this.missionSearchCriteria = searchCriteria;
                if (this.viewMode === 'MAP' && timeFilters != null) {
                    criteria.startTime = timeFilters.startTime;
                    criteria.endTime = timeFilters.endTime;
                } else {
                    criteria.startTime = this.missionSearchCriteria.startTime;
                    criteria.endTime = this.missionSearchCriteria.endTime;
                }
                this.searchTimes$.next({
                    startTime: criteria.startTime,
                    endTime: criteria.endTime
                });
                return this.operationsSummaryService.findMissionLocations(
                    criteria,
                    filters
                );
            }),
            shareReplay()
        );
    }

    private setupListeners() {
        combineLatest([
            this.noticesService.change$.pipe(startWith(null)),
            this.searchTimes$,
            this.currentOrganisation$
        ])
            .pipe(
                filter(
                    ([changes, times, organisation]) =>
                        times != null && organisation != null
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(([changes, times, organisation]) => {
                this.refreshLotams(times.startTime, times.endTime);
            });

        this.missionLocations$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(locations => {
                if (locations == null) {
                    return;
                }
                const [pointLocations, polygonLocations] =
                    missionLocationsToFeatureCollection(
                        locations,
                        this.dateFormatService
                    );
                this.mapData$.next({
                    ...this.mapData$.getValue(),
                    MISSIONS: polygonLocations,
                    MISSION_MARKERS: pointLocations
                });
            });

        this.missionNoticesDataService.lotams$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(lotams => {
                this.mapData$.next({
                    ...this.mapData$.getValue(),
                    LOTAM: noticeLocationsToFeatureCollection(
                        lotams,
                        this.currentOrganisation.timeZone
                    )
                });
            });

        this.missionViewService.permissionsUpdated$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(updated => {
                if (updated) {
                    this.canAddMission = this.missionViewService.canAddMission;
                }
            });

        combineLatest([
            this.missionViewService.viewMode$,
            this.missionLocations$,
            this.currentOrganisation$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([viewMode, locationList, currentOrganisation]) => {
                const oldViewMode = this.viewMode;
                this.viewMode = viewMode;
                if (
                    oldViewMode !== this.viewMode &&
                    viewMode === 'MAP' &&
                    currentOrganisation != null
                ) {
                    this.handleOperationsViewTime();
                    this.setupMissionListReports();
                } else if (
                    oldViewMode !== this.viewMode &&
                    viewMode === 'BOTH'
                ) {
                    this.updatedTimeFilter$.next(null);
                }
                this.featuresSource.next([
                    // {
                    //     id: FLIGHT_AREA_FEATURE_GROUP_ID,
                    //     name: 'Flight Areas',
                    //     type: 'Point',
                    //     canAdd: false,
                    //     existingFeatures:
                    //         viewMode === 'MAP'
                    //             ? []
                    //             : locationList.map(l => ({
                    //                   id: l.id,
                    //                   name: l.name,
                    //                   geom: l.representativePoint
                    //               }))
                    // },
                    {
                        id: CHECKER_FEATURE_GROUP_ID,
                        name: 'Checker Flight Area',
                        type: 'Polygon',
                        canAdd: false,
                        existingFeatures: []
                    }
                ] as FeatureGroup[]);
            });

        this.mapClicked$
            .pipe(delay(10))
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(coordinates => {
                if (
                    !this.featureSelected$.getValue() &&
                    !this.isDrawing &&
                    !this.mapIsInEditMode
                ) {
                    this.addAirspaceCheckerFlightArea(coordinates);
                } else {
                    this.clearFlightArea();
                }
                this.isDrawing = this.map.draw.getMode() !== 'simple_select';
            });

        this.shouldShowAirspaceDisclaimer$
            .pipe(debounceTime(500), takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.showAirspaceDisclaimerDialogue());
    }

    private refreshLotams(startTime: string, endTime: string) {
        if (this.canUseNotices) {
            const dateRange: DateRange = {
                start: startTime,
                end: endTime
            };
            this.missionNoticesDataService.refreshNotices(
                dateRange,
                this.currentOrganisation.id
            );
        } else {
            this.missionNoticesDataService.resetNotices();
        }
    }

    private setupTimeZoneHandlers() {
        this.mapCenter$
            .pipe(
                filter(
                    viewport =>
                        viewport != null && this.timeZoneSource === 'MAP'
                ),
                debounceTime(1000),
                switchMap(viewport =>
                    this.geospatialService.findTimeZone(
                        viewport.latitude,
                        viewport.longitude
                    )
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: timezone => {
                    this.currentTimeZone =
                        timezone?.timeZone != null
                            ? timezone.timeZone
                            : this.currentTimeZone;
                    this.changeDetector.markForCheck();
                },
                // The errors here normally spring from the backend due to issues with coordinates
                // We can ignore them and the timezone will correct itself the next time the map moves,
                // or once the user starts planning
                error: (error: FlyFreelyError) => DO_NOTHING
            });
    }

    setupMissionListReports() {
        this.missionReportService
            .findAvailableMissionListTemplates(
                'MissionList',
                this.currentOrganisation.id
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: templates => {
                    this.missionReportTemplates = templates;
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while fetching mission list report templates: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    handleOperationsViewTime() {
        // .startOf and .endOf mutates the value, so for start and end each has to use their own moment instance.
        const start = moment()
            .tz(this.currentOrganisation?.timeZone ?? defaultTimezone)
            .startOf('d');
        const end = moment()
            .tz(this.currentOrganisation?.timeZone ?? defaultTimezone)
            .endOf('d');
        switch (this.timeSelection) {
            case '+2':
                this.updatedTimeFilter$.next({
                    startTime: toTimestamp(start.toDate()),
                    endTime: toTimestamp(end.add(2, 'days').toDate())
                });
                break;
            case '+7':
                this.updatedTimeFilter$.next({
                    startTime: toTimestamp(start.toDate()),
                    endTime: toTimestamp(end.add(7, 'days').toDate())
                });
                break;
            case 'CUSTOM':
                const customDates = this.dateRangeForm.value.customDateRange;
                this.searchCustomDate(customDates);
                break;

            // The default will then be TODAY
            default:
                this.updatedTimeFilter$.next({
                    startTime: toTimestamp(start.toDate()),
                    endTime: toTimestamp(end.toDate())
                });
                break;
        }
    }

    searchCustomDate(customDates: Date[]) {
        this.timeSelection = 'CUSTOM';
        if (customDates == null) {
            this.timeSelection = 'TODAY';
            this.handleOperationsViewTime();
            return;
        }
        const start = moment(customDates[0])
            .tz(this.currentOrganisation?.timeZone ?? defaultTimezone)
            .startOf('d');
        const end = moment(customDates[1])
            .tz(this.currentOrganisation?.timeZone ?? defaultTimezone)
            .endOf('d');
        this.updatedTimeFilter$.next({
            startTime: toTimestamp(start.toDate()),
            endTime: toTimestamp(end.toDate())
        });
    }

    addAirspaceCheckerFlightArea(coordinates: LngLat) {
        const point: Point = {
            type: 'Point',
            coordinates: [coordinates.lng, coordinates.lat]
        };
        this.flightArea = circle(point, this.checkerAreaRadius, {
            units: 'meters'
        }).geometry;
        const feature: LocationFeatureDto = {
            type: 'FLIGHT_AREA',
            name: 'Flight Area',
            geometry: this.flightArea
        };
        const mapFeatures = this.featuresSource.getValue();
        const features = mapFeatures.map(fg =>
            fg.id === 1
                ? {
                      ...fg,
                      // override existing feature because we only want one checker flight area
                      existingFeatures: [toMapFeature(feature, -10)]
                  }
                : fg
        );
        this.skipNextFeatureUpdateZoom = true;
        this.featuresSource.next(features);
        this.map.zoomToFeature(toMapFeature(feature, -10), false);
    }

    removeAirspaceCheckerFlightArea() {
        const features = this.featuresSource.getValue().map(fg =>
            fg.id === CHECKER_FEATURE_GROUP_ID
                ? {
                      ...fg,
                      existingFeatures: []
                  }
                : fg
        );
        this.featuresSource.next(features);
    }

    onCheckerParametersChanged(value: AirspaceCheckerParametersWithStartEnd) {
        if (
            value.radius != this.checkerAreaRadius &&
            value.radius != null &&
            this.flightArea != null
        ) {
            this.checkerAreaRadius = value.radius;
            const point = center(this.flightArea);
            this.removeAirspaceCheckerFlightArea();
            this.addAirspaceCheckerFlightArea(
                pointToLngLat(point.geometry.coordinates)
            );
        }
        this.airspaceValue = value;
        this.changeDetector.detectChanges();
    }

    onMeasurementToolSelected() {
        this.isDrawing = true;
    }

    onEditModeChanged(editing: boolean) {
        this.mapIsInEditMode = editing;
    }

    onClusterFeatureSelected(feature: MapFeature) {
        this.featureSelected$.next(true);
        this.selectedLocation = feature;
        this.onFeatureSelected({ groupId: this.cluster.groupId, feature });
    }

    onFeatureSelected(selected: FeatureAndGroup) {
        this.featureSelected$.next(true);
        this.selectedMissions = null;
        this.selectedLocation = null;
        if (
            selected == null ||
            selected.groupId !== FLIGHT_AREA_FEATURE_GROUP_ID
        ) {
            return;
        }
        this.selectedLocation = selected.feature;
        const doneWorking = this.workTracker.createTracker();
        this.operationsSummaryService
            .findMissionsAtLocation(<number>selected.feature.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: missions => {
                    this.selectedMissions = this.preferencesService
                        .showDummyData
                        ? missions
                        : missions.filter(mission => mission.isDummy === false);
                    doneWorking();
                },
                error: (error: FlyFreelyError) =>
                    this.toastr.error(
                        `Error refreshing missions for location: ${error.message}`
                    )
            });
    }

    onMapReady() {
        if (this.firstRun) {
            this.firstRun = false;
            this.map.zoomToAllFeatures(true);
        }
    }

    onFeatureClusterSelected(cluster: FeaturesAndGroup) {
        this.featureSelected$.next(true);
        // this.selectedMissions = null;
        this.selectedLocation = null;
        this.cluster = cluster;
        this.changeDetector.detectChanges();
    }

    onFeatureUnselected() {
        this.featureSelected$.next(false);
    }

    onMapClicked(coordinates: LngLat) {
        if (!this.awaitingFeature) {
            return;
        }
        this.mapClicked$.next(coordinates);
    }

    setAwaitingFeature(awaitingFeature: boolean) {
        this.awaitingFeature = awaitingFeature;
    }

    clearFlightArea() {
        this.removeAirspaceCheckerFlightArea();
        this.flightArea = null;
    }

    createMissionFromChecker() {
        const featureGroup = this.featuresSource
            .getValue()
            .find(fg => fg.id === 1);
        if (featureGroup?.existingFeatures?.length === 0) {
            return;
        }
        const geom = featureGroup.existingFeatures[0].geom;
        if (geom.type !== 'Polygon') {
            return;
        }

        const name = `Mission ${moment().format(
            this.dateFormatService.getDateFormat()
        )}`;

        const features: LocationFeatureDto[] = [
            {
                geometry: geom,
                name: 'Flight Area',
                type: LocationFeatureDto.Type.FLIGHT_AREA
            }
        ];

        const createCommand: CreateLocationCommand = {
            name,
            features,
            organisationId: this.currentOrganisation.id,
            type: CreateLocationCommand.Type.MISSION,
            derivedFromId: null
        };

        if (this.airspaceValue == null) {
            this.logging.warn('No airspace check details to make a mission');
            return;
        }

        const tz = this.airspaceValue.airspaceTimeZone ?? defaultTimezone;

        const t = moment(
            moment(this.airspaceValue.airspaceDate ?? new Date()).format(
                'YYYY-MM-DD[T]HH:mm:ss'
            )
        ).tz(tz);
        const utc = t.toISOString();

        combineLatest([
            this.locationService.createLocation(createCommand),
            this.organisationService.findByIdForUser(
                this.currentOrganisation.id,
                this.currentOrganisation.id
            )
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([location, organisation]) => {
                const mission = this.missionService.newMission(organisation);
                mission.location = location;
                mission.name = createCommand.name;
                mission.missionDate = utc;
                mission.timeZone = this.airspaceValue.airspaceTimeZone;
                mission.missionEstimatedTime =
                    this.airspaceValue.airspaceDuration;
                mission.maximumHeight = this.airspaceValue.airspaceHeight;
                mission.timeZone = location.timeZone;
                mission.lockedFields = {
                    aircraft: false,
                    isDummy: false,
                    missionCrew: false,
                    missionType: false,
                    missionWorkflowVersion: false,
                    timeZone: false
                };
                this.missionDialoguesService.showMissionEditor(
                    mission,
                    organisation
                );
            })
            .add(this.workTracker.createTracker());
    }

    showMission(mission: MissionSummaryDto) {
        this.missionDialoguesService.showMissionDetails(mission.id, true);
    }

    showMissionTableWithCurrent() {
        const updatedTimes = this.updatedTimeFilter$.getValue();
        this.angulartics2.eventTrack.next({
            action: 'show-missions',
            properties: {
                category: 'daily-operations'
            }
        });
        this.missionSearchUpdated.emit(updatedTimes);
        this.missionViewService.setViewMode('BOTH');
    }

    runMissionReport(templateId: number) {
        const doneWorking = this.workTracker.createTracker();

        this.angulartics2.eventTrack.next({
            action: 'download-mission-list-report',
            properties: {
                category: 'daily-operations',
                label: templateId
            }
        });

        const { startTime, endTime } = this.updatedTimeFilter$.getValue();
        this.missionViewDataService.searchCriteria$
            .pipe(take(1), takeUntil(this.ngUnsubscribe$))
            .subscribe(criteria => {
                const { organisationId, orOrganisationId, authorityIds } =
                    criteria;
                this.downloadService
                    .downloadFromUrl(
                        this.missionReportService.getMissionsPdfUrl(
                            templateId,
                            organisationId,
                            orOrganisationId,
                            // If the orOrganisationId has a value the normal organisationId will be null, so fall back to the former.
                            organisationId ?? orOrganisationId,
                            startTime,
                            endTime,
                            authorityIds
                        )
                    )
                    .then(() => doneWorking())
                    .catch(() => doneWorking());
            });
    }

    onFullscreenRequested() {
        this.fullscreenService.toggleFullScreen(this.elementRef);
    }

    onUpdateJurisdiction(jurisdiction: AirspaceJurisdictionDto) {
        this.jurisdictionHasAirspaceChecker = jurisdiction?.hasAirspaceChecker;
        this.airspaceJurisdiction = jurisdiction;
        this.showAirspaceDisclaimer = false;
        this.airspaceDisclaimerDates = {};
        this.shouldShowAirspaceDisclaimer$.next();
    }

    onViewportChange(viewport: MapViewport) {
        this.mapCenter$.next(viewport);
    }

    onAirspaceCheckStatusChange(hasCheckResult: boolean) {
        this.timeZoneSource = hasCheckResult ? 'AIRSPACE_CHECK' : 'MAP';
    }

    showAirspaceDisclaimerDialogue() {
        const disclaimer = this.airspaceJurisdiction?.airspaceDisclaimer;
        const airspaceJurisdictionId = this.airspaceJurisdiction?.id;
        if (
            airspaceJurisdictionId == null ||
            disclaimer == null ||
            disclaimer.length == 0
        ) {
            this.showAirspaceDisclaimer = false;
            return;
        }
        this.preferencesService
            .findPreferencesAsOption(AIRSPACE_DISCLAIMER, null)
            .pipe(
                map(p =>
                    getOrElse(() => ({
                        [airspaceJurisdictionId]: moment(new Date())
                            .subtract(1, 'day')
                            .toDate()
                    }))(p)
                ),
                takeUntil(this.ngUnsubscribe$),
                take(1)
            )
            .subscribe({
                next: preferences => {
                    this.airspaceDisclaimerDates = preferences;
                    if (
                        preferences == null ||
                        preferences[airspaceJurisdictionId] == null
                    ) {
                        this.showAirspaceDisclaimer = true;
                        return;
                    }
                    const today = moment(new Date()).format('YYYY-MM-DD');
                    const lastViewed = moment(
                        preferences[airspaceJurisdictionId]
                    ).format('YYYY-MM-DD');
                    // Only show the disclaimer once per account
                    this.showAirspaceDisclaimer =
                        moment(lastViewed).isBefore(today);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(error);
                }
            });
    }

    onAirspaceDisclaimerAcknowledged() {
        this.showAirspaceDisclaimer = false;
        const airspaceJurisdictionId = this.airspaceJurisdiction?.id;
        this.preferencesService
            .updatePreferences(AIRSPACE_DISCLAIMER, null, {
                ...this.airspaceDisclaimerDates,
                [airspaceJurisdictionId]: new Date()
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(DO_NOTHING)
            .add(this.workTracker.createTracker());
    }

    zoomToGeometry(point: Geometry) {
        this.map.zoomToGeometries([point]);
    }

    /**
     * Observe the window for size changes so we can dynamically collapse the filter buttons
     */
    private setupWindowSizingHandler() {
        window.addEventListener('resize', () => {
            this.handleButtonSizing();
        });
    }

    /**
     * Collapse the filter buttons regardless of mobile view mode
     */
    private handleButtonSizing() {
        this.reduceButtonSizes =
            this.useMobileLayout ||
            window.innerWidth < 1740 ||
            window.innerWidth < 1460;
    }

    /**
     * Check if the current device is a mobile device to improve layout
     */
    refreshMobileMode() {
        this.useMobileLayout =
            this.deviceDetectorService.isMobile() ||
            this.deviceDetectorService.isTablet();
    }
}
